import React from 'react';

import {
    BrowseAggregatorContent,
    BrowseAggregatorEntityType,
    CertificationBrowseAggregatorContentUtil,
} from '@udemy/browse-aggregator';
import {
    CourseDirectoryContextProvider,
    CourseSearchQuery,
    CourseSearchQueryVariables,
} from '@udemy/react-discovery-units';
import {CourseSearchTriggerType} from '@udemy/react-discovery-units';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';

import {useCertificationBrowseAggregatorContext} from '../../../contexts/certification-browse-page-content-context';

export const CERTIFICATION_PREP_SEARCH_QUERY = 'Certification Preparation';

// Helper function to get the search query from the BrowsePageContent; can be used in getStaticProps/getServerSideProps
export const getCourseSearchQueryVariables = (
    aggregatorContent?: BrowseAggregatorContent,
): CourseSearchQueryVariables => {
    if (!aggregatorContent) {
        return {
            queryString: '',
            context: {
                triggerType: CourseSearchTriggerType.BrowseCertification,
            },
            page: 0,
        };
    }
    const certificationBrowseAggregatorContentUtil = new CertificationBrowseAggregatorContentUtil(
        aggregatorContent,
    );
    const queryString =
        certificationBrowseAggregatorContentUtil.getAssignedEntityByType(
            BrowseAggregatorEntityType.SEARCH_QUERY,
        )?.entity_value ?? '';
    const topicId = certificationBrowseAggregatorContentUtil.getAssignedEntityByType(
        BrowseAggregatorEntityType.TOPIC,
    )?.entity_value;
    const context = {
        triggerType: CourseSearchTriggerType.BrowseCertification,
    };
    const filters = topicId ? {topicIds: [topicId]} : undefined;
    return {
        queryString,
        filters,
        context,
        page: 0,
    };
};

export interface AggregatorCourseDirectoryContextProviderProps {
    children: React.ReactNode;
    searchQueryOverride?: string;
    initialCourseSearchResponse?: CourseSearchQuery;
}

/**
 * CourseDirectoryContextProvider wrapper that pulls in the search query from the aggregator content and
 * subscriber status from the subscription plan context.
 * @param children
 * @param searchQueryOverride
 * @param initialCourseSearchResponse
 * @constructor
 */
export const AggregatorCourseDirectoryContextProvider: React.FC<
    AggregatorCourseDirectoryContextProviderProps
> = ({children, searchQueryOverride, initialCourseSearchResponse}) => {
    const certificationBrowseAggregatorContext = useCertificationBrowseAggregatorContext();
    const searchQuery =
        searchQueryOverride ??
        certificationBrowseAggregatorContext?.assignedEntities?.searchQuery?.entity_value ??
        '';
    const searchQueryOptions = React.useMemo(
        () => ({
            queryString: searchQuery,
            context: {triggerType: CourseSearchTriggerType.BrowseCertification},
        }),
        [searchQuery],
    );

    const consumerSubscriptionPlan = useConsumerSubscriptionPlan();
    const isPersonalPlanSubscriber =
        !!consumerSubscriptionPlan &&
        !consumerSubscriptionPlan.isLoading &&
        consumerSubscriptionPlan?.isSubscriber;

    const courseSearchContext = React.useMemo(
        () => ({
            originalQuery: searchQuery,
            triggerType: CourseSearchTriggerType.BrowseCertification,
        }),
        [searchQuery],
    );

    return (
        <CourseDirectoryContextProvider
            searchQueryOptions={searchQueryOptions}
            isPersonalPlanSubscriber={isPersonalPlanSubscriber}
            initialCourseSearchResponse={initialCourseSearchResponse}
            courseSearchContext={courseSearchContext}
        >
            {children}
        </CourseDirectoryContextProvider>
    );
};
